import ForceGraph from 'force-graph';
import { formatSIMC, shortAddress } from './format.js';
import { showTooltip, moveTooltip, hideTooltip } from './tooltip.js';
import {
  nodeVal,
  nodeRadius,
  edgeWidth,
  particleWidthForPayment,
  hexToRgba,
  edgeKey,
  resolveLinkForPayment,
} from './graph-math.js';

const PULSE_DURATION_MS = 2000;
const PARTICLE_BASE_SIZE = 2.5;
const LABEL_INK_SECONDARY = '#c3c2b7';

export function createGraph(container, store, { reducedMotion = false, autoFit = false } = {}) {
  const nodeById = new Map(); // address -> node do force-graph (posição persiste)
  const linkByKey = new Map(); // "from|to" -> link do force-graph

  let lastPointer = { x: 0, y: 0 };
  let hoveredNode = null;
  let hoveredLink = null;

  // Captura global, fase de captura: garante que a posição do ponteiro está
  // atualizada ANTES dos handlers internos do force-graph (mousemove no
  // canvas) rodarem — senão o tooltip usa a posição do frame anterior.
  const onPointerMove = (event) => {
    lastPointer = { x: event.clientX, y: event.clientY };
    if (hoveredNode) {
      moveTooltip(lastPointer.x, lastPointer.y);
    } else if (hoveredLink) {
      moveTooltip(lastPointer.x, lastPointer.y);
    }
  };
  document.addEventListener('pointermove', onPointerMove, true);

  const fg = new ForceGraph(container)
    .backgroundColor('rgba(0,0,0,0)')
    .graphData({ nodes: [], links: [] })
    .nodeId('id')
    .nodeVal((n) => nodeVal(n.data))
    .nodeRelSize(4)
    .autoPauseRedraw(false) // precisa continuar redesenhando pro fade do ring de pulso
    .minZoom(0.35)
    .maxZoom(4)
    .linkSource('source')
    .linkTarget('target')
    .linkWidth((l) => edgeWidth(l.data))
    .linkColor((l) => hexToRgba(l.data.__fromColor, 0.35))
    .linkDirectionalParticleSpeed(0.01)
    .linkDirectionalParticleWidth((l) => l.__particleWidth ?? PARTICLE_BASE_SIZE)
    .linkDirectionalParticleColor((l) => l.__particleColor ?? '#ffffff')
    .nodeCanvasObject((node, ctx, globalScale) => {
      const data = node.data;
      if (!data) return;
      const r = nodeRadius(data);
      const color = data.agent.color;

      // ring de pulso (fade ~2s) ao receber pagamento
      if (node.lastPulseAt) {
        const elapsed = Date.now() - node.lastPulseAt;
        if (elapsed < PULSE_DURATION_MS) {
          const t = elapsed / PULSE_DURATION_MS;
          ctx.save();
          ctx.globalAlpha = 1 - t;
          ctx.lineWidth = 2 / globalScale;
          ctx.strokeStyle = color;
          ctx.beginPath();
          ctx.arc(node.x, node.y, r + 4 + t * 6, 0, 2 * Math.PI);
          ctx.stroke();
          ctx.restore();
        } else {
          node.lastPulseAt = null;
        }
      }

      // círculo do agente
      ctx.beginPath();
      ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
      ctx.fillStyle = hexToRgba(color, 0.85);
      ctx.fill();

      // emoji centralizado
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = `${Math.max(8, r)}px sans-serif`;
      ctx.fillText(data.agent.emoji, node.x, node.y);

      // label abaixo, em ink secundário (não na cor da série)
      const fontSize = 11 / Math.sqrt(globalScale);
      ctx.font = `${fontSize}px system-ui, sans-serif`;
      ctx.fillStyle = LABEL_INK_SECONDARY;
      ctx.fillText(data.agent.name, node.x, node.y + r + fontSize + 2);
    })
    .nodePointerAreaPaint((node, color, ctx) => {
      if (!node.data) return;
      const r = nodeRadius(node.data);
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
      ctx.fill();
    })
    .onNodeHover((node) => {
      hoveredNode = node;
      hoveredLink = null;
      container.style.cursor = node ? 'pointer' : 'default';
      if (node) {
        showTooltip(nodeTooltipHtml(node.data), lastPointer.x, lastPointer.y);
      } else {
        hideTooltip();
      }
    })
    .onLinkHover((link) => {
      hoveredLink = link;
      if (hoveredNode) return;
      if (link) {
        showTooltip(linkTooltipHtml(link), lastPointer.x, lastPointer.y);
      } else {
        hideTooltip();
      }
    })
    .onNodeClick((node) => {
      fg.centerAt(node.x, node.y, 600);
      fg.zoom(Math.max(fg.zoom(), 2.2), 600);
    })
    .onNodeDrag(() => hideTooltip())
    .onBackgroundClick(() => hideTooltip());

  // autoFit (embed): sempre que a física assenta (inclusive após nós novos),
  // re-enquadra o grafo inteiro no container — nós nunca cortam na borda.
  if (autoFit) fg.onEngineStop(() => fg.zoomToFit(400, 40));

  // física ajustada pra layout estável e elegante com poucos nós
  fg.d3Force('charge').strength(-220);
  fg.d3Force('link').distance(130).strength(0.5);
  fg.d3Force('center').strength(0.05);
  fg.d3VelocityDecay(0.45);

  function nodeTooltipHtml(data) {
    const a = data.agent;
    return (
      `<div class="tooltip__title">${a.emoji} ${a.name}</div>` +
      `<div class="tooltip__row">${a.role || ''}</div>` +
      `<div class="tooltip__row">${shortAddress(a.address)}</div>` +
      `<div class="tooltip__row">Enviado: ${formatSIMC(data.totalSent)} SIMC</div>` +
      `<div class="tooltip__row">Recebido: ${formatSIMC(data.totalReceived)} SIMC</div>`
    );
  }

  function linkTooltipHtml(link) {
    const d = link.data;
    const fromAgent = link.source?.data?.agent;
    const toAgent = link.target?.data?.agent;
    const fromLabel = fromAgent ? `${fromAgent.emoji} ${fromAgent.name}` : shortAddress(d.from);
    const toLabel = toAgent ? `${toAgent.emoji} ${toAgent.name}` : shortAddress(d.to);
    return (
      `<div class="tooltip__title">${fromLabel} → ${toLabel}</div>` +
      `<div class="tooltip__row">Volume: ${formatSIMC(d.volume)} SIMC</div>` +
      `<div class="tooltip__row">Pagamentos: ${d.count}</div>`
    );
  }

  function syncData() {
    for (const node of store.nodes.values()) {
      let fgNode = nodeById.get(node.address);
      if (!fgNode) {
        fgNode = { id: node.address, lastPulseAt: null };
        nodeById.set(node.address, fgNode);
      }
      fgNode.data = node;
    }

    for (const edge of store.edges.values()) {
      let link = linkByKey.get(edge.key);
      if (!link) {
        link = { source: edge.from, target: edge.to };
        linkByKey.set(edge.key, link);
      }
      link.data = edge;
      // cor da aresta = cor do remetente (fixa por edge, atualiza se o nó já existir)
      const fromNode = nodeById.get(edge.from);
      if (fromNode?.data) link.data.__fromColor = fromNode.data.agent.color;
    }

    fg.graphData({
      nodes: [...nodeById.values()],
      links: [...linkByKey.values()],
    });
  }

  /**
   * Consumido a cada pagamento novo (fluxo ao vivo — nunca no backfill
   * inicial): dispara a partícula na aresta correspondente e o ring de
   * pulso no nó destinatário.
   */
  function onNewPayment(payment) {
    const link = resolveLinkForPayment(linkByKey, payment);
    const toNode = nodeById.get(payment.to);
    const fromNode = nodeById.get(payment.from);
    if (!link || !toNode || !fromNode) return;

    const color = fromNode.data?.agent.color ?? '#ffffff';

    // respeita prefers-reduced-motion (usado no /embed): sem partícula
    // viajante, só o pulso (fade simples) no nó destinatário.
    if (!reducedMotion) {
      link.__particleWidth = particleWidthForPayment(payment.value);
      link.__particleColor = hexToRgba(color, 1);
      fg.emitParticle(link);
    }

    toNode.lastPulseAt = Date.now();
  }

  function resize() {
    const w = container.clientWidth || 800;
    const h = container.clientHeight || 480;
    fg.width(w).height(h);
    if (autoFit) fg.zoomToFit(300, 40);
  }

  const resizeObserver = new ResizeObserver(() => resize());
  resizeObserver.observe(container);
  resize();

  return {
    syncData,
    pulsePayment: onNewPayment,
    onNewPayment,
    // enquadra o grafo no viewport (chamar após o backfill, quando a física assentou)
    fitView: (ms = 400) => fg.zoomToFit(ms, 60),
    destroy: () => {
      resizeObserver.disconnect();
      document.removeEventListener('pointermove', onPointerMove, true);
      fg._destructor?.();
    },
  };
}
